import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAMedias } from '../../context/AMediasContext';
import { useAvisosAMedias } from '../../context/AvisosAMediasContext';
import { AvisosAMedias } from '../../components/amedias/AvisosAMedias';
import { useAuth } from '../../context/AuthContext';
import { Plan } from '../../types';
import { esCero, formatoEuros } from '../../utils/amediasUtils';
import { estadoRecordatorio } from '../../utils/recordatoriosAMedias';
import { Plus, Split, ChevronDown, ChevronRight, Archive } from 'lucide-react';
import { ModalPlan } from '../../components/amedias/ModalPlan';
import { useLimite } from '../../hooks/useLimite';
import { ContadorLimite } from '../../components/plus/ContadorLimite';

export const fechasPlan = (p: Plan) => {
  const f = (d: Date) => format(d, "d MMM yyyy", { locale: es });
  if (p.fechaInicio && p.fechaFin) return `${f(p.fechaInicio)} – ${f(p.fechaFin)}`;
  if (p.fechaInicio) return `Desde el ${f(p.fechaInicio)}`;
  if (p.fechaFin) return `Hasta el ${f(p.fechaFin)}`;
  return '';
};

/** Tarjeta de un plan con tu saldo. Los movimientos los vigila AvisosAMedias. */
const TarjetaPlan: React.FC<{ plan: Plan }> = ({ plan }) => {
  const { user } = useAuth();
  const estado = useAvisosAMedias().estadoPlan(plan.id);
  const cargando = !estado || estado.cargando;
  const gastos = useMemo(() => estado?.gastos ?? [], [estado]);
  const yo = plan.participantes.find(p => p.uid === user?.uid);

  const total = useMemo(() => gastos.reduce((s, g) => s + g.importe, 0), [gastos]);
  const miSaldo = estado?.miSaldo ?? 0;
  // Pasado el plazo de recordatorio del plan, quien debe lo ve resaltado
  const tocaPagar = miSaldo < 0 && estadoRecordatorio(plan, gastos).vencido;

  return (
    <Link to={`/amedias/${plan.id}`}
      className={`flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border transition
                  hover:border-emerald-400 dark:hover:border-emerald-600 ${
                    plan.archivado ? 'border-dashed border-gray-300 dark:border-gray-700 opacity-75'
                                   : 'border-gray-200 dark:border-gray-700'}`}>
      <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-2xl">
        {plan.icono}
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="font-bold text-gray-900 dark:text-white truncate">{plan.nombre}</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {[fechasPlan(plan), `${plan.participantes.length} personas`, !cargando && `${formatoEuros(total)} en total`]
            .filter(Boolean).join(' · ')}
        </p>
      </div>
      <div className="text-right shrink-0">
        {!yo ? (
          <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Di quién eres</span>
        ) : cargando ? (
          <span className="text-xs text-gray-400">…</span>
        ) : esCero(miSaldo) ? (
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Estás al día</span>
        ) : (
          <>
            <p className={`text-[11px] ${miSaldo > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {miSaldo > 0 ? 'te deben' : tocaPagar ? '⏰ toca pagar' : 'debes'}
            </p>
            <p className={`font-bold tabular-nums ${miSaldo > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatoEuros(Math.abs(miSaldo))}
            </p>
          </>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
    </Link>
  );
};

export const Planes: React.FC = () => {
  const { planes, loadingPlanes } = useAMedias();
  const navigate = useNavigate();
  // Solo cuentan los que organizas: a los que te invitan se entra siempre
  const limite = useLimite('planesAMedias');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [verArchivados, setVerArchivados] = useState(false);

  const activos = planes.filter(p => !p.archivado);
  const archivados = planes.filter(p => p.archivado);

  if (loadingPlanes) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">A medias</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Gastos compartidos de viajes, comidas y lo que surja.{' '}
            <Link to="/ayuda#amedias" className="font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">
              ¿Cómo funciona?
            </Link>
          </p>
        </div>
        <button onClick={() => limite.intentar(() => setModalAbierto(true))}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg
                     bg-emerald-600 hover:bg-emerald-700 text-white transition">
          <Plus className="w-4 h-4" /> Nuevo plan <ContadorLimite texto={limite.texto} />
        </button>
      </header>

      <AvisosAMedias />

      {activos.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <Split className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            {planes.length === 0 ? 'Todavía no tienes ningún plan.' : 'No tienes planes en marcha.'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1 max-w-sm mx-auto">
            Crea uno para un viaje o una cena, apunta quién paga qué y la app te dice quién le debe a quién.
          </p>
          <button onClick={() => limite.intentar(() => setModalAbierto(true))}
            className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-sm font-semibold rounded-lg
                       bg-emerald-600 hover:bg-emerald-700 text-white transition">
            <Plus className="w-4 h-4" /> Crear el primero
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {activos.map(p => <TarjetaPlan key={p.id} plan={p} />)}
        </div>
      )}

      {archivados.length > 0 && (
        <div className="mt-6">
          <button onClick={() => setVerArchivados(v => !v)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
            <Archive className="w-4 h-4" /> Archivados ({archivados.length})
            <ChevronDown className={`w-4 h-4 transition ${verArchivados ? 'rotate-180' : ''}`} />
          </button>
          {verArchivados && (
            <div className="space-y-2 mt-2">
              {archivados.map(p => <TarjetaPlan key={p.id} plan={p} />)}
            </div>
          )}
        </div>
      )}

      {modalAbierto && (
        <ModalPlan onClose={() => setModalAbierto(false)} onCreado={id => navigate(`/amedias/${id}`)} />
      )}
    </div>
  );
};
